import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { closeHistory } from '@tiptap/pm/history';
import { TextSelection } from '@tiptap/pm/state';
import { formatCurrency } from '@/lib/format';
import type { InvestmentRowVersionChange } from '@/store/investmentInspectorStore';

/** The form and inline editor write the same row cells and use the same auto-calculation. */
export function setInvestmentRowCell(
  editor: Editor,
  row: PMNode,
  pos: number,
  cellIndex: number,
  value: string
) {
  if (
    row.attrs.investmentProduct ||
    row.attrs.productPrice ||
    cellIndex < 0 ||
    cellIndex >= row.childCount
  )
    return;
  let cellPos = pos + 1;
  for (let index = 0; index < cellIndex; index++) cellPos += row.child(index).nodeSize;
  const cell = row.child(cellIndex);
  const text = cellIndex === 0 ? value : value.trim();
  if (cell.textContent === text) return;
  const paragraph = editor.schema.nodes.paragraph.create(
    null,
    text ? editor.schema.text(text) : null
  );
  const tr = editor.state.tr;
  const attrs = {
    ...row.attrs,
    ...(cellIndex > 0 && row.attrs.productId ? { hasOverrides: true } : {}),
    ...(cellIndex === 1 && row.childCount === 4 ? { rateKey: 'custom' } : {}),
  };
  if (
    (cellIndex > 0 && row.attrs.productId && !row.attrs.hasOverrides) ||
    (cellIndex === 1 && row.childCount === 4 && row.attrs.rateKey !== 'custom')
  )
    tr.setNodeMarkup(pos, undefined, attrs);
  // Keep the text replacement last so subsequent keystrokes form one undo event.
  tr.replaceWith(cellPos + 1, cellPos + cell.nodeSize - 1, paragraph);
  tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 3)));
  editor.view.dispatch(tr);
}

/** A rate choice changes its identity and snapshot together, in one undoable transaction. */
export function setInvestmentRowRate(
  editor: Editor,
  row: PMNode,
  pos: number,
  rateKey: string,
  amountCents?: number,
  currency = 'EUR'
) {
  if (row.childCount !== 4 || row.attrs.investmentProduct || row.attrs.productPrice) return;
  const tr = closeHistory(editor.state.tr);
  if (amountCents !== undefined) {
    if (!Number.isFinite(amountCents) || amountCents < 0) return;
    const cell = row.child(1);
    const cellPos = pos + 1 + row.firstChild!.nodeSize;
    const paragraph = editor.schema.nodes.paragraph.create(
      null,
      editor.schema.text(formatCurrency(amountCents / 100, currency))
    );
    tr.replaceWith(cellPos + 1, cellPos + cell.nodeSize - 1, paragraph);
  }
  tr.setNodeMarkup(pos, undefined, {
    ...row.attrs,
    rateKey,
    ...(amountCents !== undefined && row.attrs.productId ? { hasOverrides: true } : {}),
  });
  tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 3)));
  editor.view.dispatch(tr);
  editor.view.dispatch(closeHistory(editor.state.tr).setMeta('addToHistory', false));
}

export function setInvestmentRowShowHours(
  editor: Editor,
  row: PMNode,
  pos: number,
  showHours: boolean
) {
  if (row.childCount !== 4 || row.attrs.investmentProduct || row.attrs.productPrice) return;
  editor.view.dispatch(
    closeHistory(editor.state.tr).setNodeMarkup(pos, undefined, { ...row.attrs, showHours })
  );
  editor.view.dispatch(closeHistory(editor.state.tr).setMeta('addToHistory', false));
}

/** Existing linked quote rows retain their version update actions in the row list. */
export function setInvestmentRowProductVersion(
  editor: Editor,
  row: PMNode,
  pos: number,
  change: InvestmentRowVersionChange
) {
  if (!row.attrs.productId || row.attrs.investmentProduct || row.attrs.productPrice) return;
  const tr = closeHistory(editor.state.tr);
  const cellStarts: number[] = [];
  let cellPos = pos + 1;
  row.forEach((cell) => {
    cellStarts.push(cellPos);
    cellPos += cell.nodeSize;
  });
  const edits = Object.entries(change.cells ?? {})
    .map(([index, value]) => [Number(index), value.trim()] as const)
    .filter(([index]) => index > 0 && index < row.childCount)
    .sort((a, b) => b[0] - a[0]);
  for (const [index, value] of edits) {
    const cell = row.child(index);
    tr.replaceWith(
      cellStarts[index] + 1,
      cellStarts[index] + cell.nodeSize - 1,
      editor.schema.nodes.paragraph.create(null, value ? editor.schema.text(value) : null)
    );
  }
  tr.setNodeMarkup(pos, undefined, {
    ...row.attrs,
    productVersionId: change.productVersionId,
    hasOverrides: change.hasOverrides,
  });
  tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 3)));
  editor.view.dispatch(tr);
  editor.view.dispatch(closeHistory(editor.state.tr).setMeta('addToHistory', false));
}
